
import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { WorkflowStackItem } from '@/types/workflow-graph';

interface WorkflowNavigationContextValue {
    /** Current workflow being displayed */
    currentWorkflow: WorkflowStackItem | null;
    /** Stack of parent workflows (for breadcrumb trail) */
    workflowStack: WorkflowStackItem[];
    /** Navigate into a sub-workflow */
    pushWorkflow: (workflow: WorkflowStackItem) => void;
    /** Navigate back to parent workflow */
    popWorkflow: () => void;
    /** Navigate to a specific workflow in the stack (by index) */
    navigateToStack: (index: number) => void;
    /** Check if we're in a nested workflow */
    isNested: boolean;
    /** Get the root workflow */
    rootWorkflow: WorkflowStackItem | null;
    /** Get the replay run ID for the current workflow (if in replay mode) */
    currentReplayRunId: string | null;
    /** Check if we're navigating in replay mode (any workflow in stack has replay context) */
    isInReplayNavigation: boolean;
}

const WorkflowNavigationContext = createContext<WorkflowNavigationContextValue | null>(null);

interface WorkflowNavigationProviderProps {
    children: ReactNode;
    initialWorkflow: WorkflowStackItem;
}

export function WorkflowNavigationProvider({
    children,
    initialWorkflow,
}: WorkflowNavigationProviderProps) {
    // Stack where index 0 is root, and last item is current
    const [stack, setStack] = useState<WorkflowStackItem[]>([initialWorkflow]);

    // Sync root workflow definition when parent updates it (e.g., after save+refetch).
    // Uses JSON comparison to avoid reacting to referential-only changes.
    const lastSyncedDefinitionRef = useRef<string>(JSON.stringify(initialWorkflow.definition));

    useEffect(() => {
        const newDefStr = JSON.stringify(initialWorkflow.definition);
        if (newDefStr !== lastSyncedDefinitionRef.current) {
            lastSyncedDefinitionRef.current = newDefStr;
            setStack(prev => {
                if (prev.length === 0) return prev;
                const root = prev[0];
                if (root.id === initialWorkflow.id && root.key === initialWorkflow.key) {
                    return [
                        { ...root, definition: initialWorkflow.definition },
                        ...prev.slice(1),
                    ];
                }
                return prev;
            });
        }
    }, [initialWorkflow.definition, initialWorkflow.id, initialWorkflow.key]);

    const currentWorkflow = stack[stack.length - 1] || null;
    const workflowStack = stack.slice(0, -1); // All except current
    const isNested = stack.length > 1;
    const rootWorkflow = stack[0] || null;
    
    // Get the replay run ID for the current workflow
    const currentReplayRunId = currentWorkflow?.replayRunId ?? null;
    
    // Check if any workflow in the stack has replay context
    const isInReplayNavigation = stack.some(w => w.replayRunId != null);

    const pushWorkflow = useCallback((workflow: WorkflowStackItem) => {
        setStack((prev) => [...prev, workflow]);
    }, []);

    const popWorkflow = useCallback(() => {
        setStack((prev) => {
            if (prev.length <= 1) return prev; // Don't pop root
            return prev.slice(0, -1);
        });
    }, []);

    const navigateToStack = useCallback((index: number) => {
        setStack((prev) => {
            if (index < 0 || index >= prev.length) return prev;
            return prev.slice(0, index + 1);
        });
    }, []);

    return (
        <WorkflowNavigationContext.Provider
            value={{
                currentWorkflow,
                workflowStack,
                pushWorkflow,
                popWorkflow,
                navigateToStack,
                isNested,
                rootWorkflow,
                currentReplayRunId,
                isInReplayNavigation,
            }}
        >
            {children}
        </WorkflowNavigationContext.Provider>
    );
}

export function useWorkflowNavigation() {
    const context = useContext(WorkflowNavigationContext);
    if (!context) {
        throw new Error('useWorkflowNavigation must be used within WorkflowNavigationProvider');
    }
    return context;
}

